import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from '@react-router';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  useGetContactCollectionByMagicLinkQuery,
  useCreateContactsWithoutTagsMutation,
  useGetTelemetryDataForContactCollectionGuestQuery,
} from '../../admin/contactCollector/api/ContactCollector.graphql.generated';
import { useGetContactCollectorGroupDetailsByPersonIdQuery, useUpdateContactForContactCollectorMutation } from './ContactCollectorGuest.graphql.generated';
import type { VisualType } from './shared/constants';
import { getImageSource } from './shared/utils/getImageSource';
import { useTranslation, useRouterHelper } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useSetEventHandle } from '@shared/utils/eventInfo';
import { ContactCollectorFieldRequirement } from '@graphql/generated-base';
import { useContactCollectorGuestTelemetry } from './ContactCollectorGuest.telemetry';
import { contactCollectorGuestRoutePaths } from './contactCollectorGuest.routes';
import type { ContactInputFields, Contacts, Context, FieldSettingsShape, FieldVisibility } from './shared/types';
import { deriveCountryDefaults } from '@shared/utils/address/deriveCountryDefaults';
import { mapContactToFormValues, mapFormValuesToUpdateInput } from './shared/utils/contactMappers';
import { buildAddress, emailFor, phoneNumbersFor } from './shared/utils/payloadFromFieldSettings';

/**
 * FE fallback used when the feature flag is OFF or when the BE returns no fieldSettings.
 * Mirrors today's hardcoded behavior (email required, phone optional, address required —
 * the legacy `mailingAddressRule="required"`). Intentionally diverges from the BE's
 * "fresh row" defaults (which mark address as OPTIONAL) so the FF-OFF flow stays
 * identical to the pre-feature UX.
 */
const LEGACY_FIELD_SETTINGS_FALLBACK: FieldSettingsShape = {
  email: ContactCollectorFieldRequirement.REQUIRED,
  phone: ContactCollectorFieldRequirement.OPTIONAL,
  address: ContactCollectorFieldRequirement.REQUIRED,
};

const visibilityFromRequirement = (r: ContactCollectorFieldRequirement): FieldVisibility => {
  if (r === ContactCollectorFieldRequirement.REQUIRED) return 'required';
  if (r === ContactCollectorFieldRequirement.OFF) return 'hidden';
  return 'optional';
};

export type { ContactInputFields, Contacts, Context };

const contactInitialValue: ContactInputFields = {
  firstName: '',
  lastName: '',
  prefix: '',
  suffix: '',
  phoneNumber: '',
  email: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  region: '',
  country: 'United States',
  countryCode: 'US',
  validated: false,
};

export const newMember = {
  ...contactInitialValue,
  address1: ' ',
  postalCode: ' ',
  city: ' ',
  region: ' ',
};

const initialValues: Contacts = {
  contacts: [contactInitialValue],
};

/**
 * Validation schema for contact collector form. Dynamic based on the collection's fieldSettings.
 *
 * `email` / `phone` "required" is NOT enforced here: Yup runs per array item, but the rule is
 * primary-only (+1 household members are at-most-optional). The primary requirement lives in the
 * submit gate (`primaryEmailMissing` / `primaryPhoneMissing` in Form.controller).
 *
 * Address fields (address1, postalCode, city, region) are marked required when
 * `fieldSettings.address === REQUIRED`. Non-primary members receive space-character sentinel values
 * during prefill so they satisfy the same validation, even though only the primary's address is
 * ever submitted.
 */
export const yupSchema = (tRequired: string, tInvalidEmail: string, fs: FieldSettingsShape) => {
  const addressRequired = fs.address === ContactCollectorFieldRequirement.REQUIRED;

  return Yup.object({
    contacts: Yup.array()
      .min(1)
      .of(
        Yup.object<ContactInputFields>({
          firstName: Yup.string().required(tRequired),
          lastName: Yup.string().required(tRequired),
          prefix: Yup.string(),
          suffix: Yup.string(),
          phoneNumber: Yup.string(),
          email: Yup.string().email(tInvalidEmail),
          address1: addressRequired ? Yup.string().required(tRequired) : Yup.string(),
          address2: Yup.string(),
          postalCode: addressRequired ? Yup.string().required(tRequired) : Yup.string(),
          city: addressRequired ? Yup.string().required(tRequired) : Yup.string(),
          region: addressRequired ? Yup.string().required(tRequired) : Yup.string(),
          country: Yup.string(),
          countryCode: Yup.string(),
          validated: Yup.boolean(),
        }),
      ),
  });
};

export const useContactCollectorGuestController = (magicLinkId: string) => {
  const location = useLocation();
  const [e164PhoneNumber, setE164PhoneNumber] = useState<{ [key: number]: string }>({ 0: '' });
  const [context, setContext] = useState<Context>({
    coupleNames: '',
    eventDisplayName: '',
    date: '',
    eventDateDisplayEnabled: false,
    website: '',
    websiteLinkEnabled: false,
    fontFamily: 'PT Serif',
    fontTextTransform: 'none',
    mediaType: '',
    mediaSource: '',
    firebaseId: '',
  });
  const { trackError, buttonInteracted } = useContactCollectorGuestTelemetry();
  const routeHelpers = useRouterHelper();
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const { t2 } = useTranslation('ssrServer');
  const tMetatags = t2('metatags');
  const { data, error } = useGetContactCollectionByMagicLinkQuery({
    variables: {
      magicLink: magicLinkId,
    },
    batchMode: 'fast',
    ssr: true,
    fetchPolicy: 'cache-first',
    onError: error => trackError('Query', error),
  });

  const eventId = data?.getContactCollectionByMagicLink?.eventId;
  const isContactCollectionInactive = !data?.getContactCollectionByMagicLink?.active;

  const [isFieldSettingsEnabled] = useIsFeatureEnabled('contactCollectorFieldSettingsEnabled');
  const fieldSettings: FieldSettingsShape = useMemo(
    () => (isFieldSettingsEnabled ? (data?.getContactCollectionByMagicLink?.fieldSettings ?? LEGACY_FIELD_SETTINGS_FALLBACK) : LEGACY_FIELD_SETTINGS_FALLBACK),
    [isFieldSettingsEnabled, data?.getContactCollectionByMagicLink?.fieldSettings],
  );
  const emailRule = visibilityFromRequirement(fieldSettings.email);
  const phoneNumberRule = visibilityFromRequirement(fieldSettings.phone);
  const mailingAddressRule = visibilityFromRequirement(fieldSettings.address);

  const { data: telemetryQueryData, loading: isTelemetryQueryLoading } = useGetTelemetryDataForContactCollectionGuestQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      eventId: eventId!,
    },
    skip: !eventId,
    onError: error => trackError('Query', error),
  });

  // Magic-link URLs carry no event handle, so EventInfoProvider cannot resolve the
  // event on its own — feed it the handle we already fetched for telemetry so the
  // global i18n locale syncer picks up the event's locale (translations would
  // otherwise fall back to the browser language).
  useSetEventHandle(telemetryQueryData?.eventById?.website || undefined);

  const eventLocale = telemetryQueryData?.eventById?.info.locale;
  const localeCountryDefaults = useMemo(() => deriveCountryDefaults(eventLocale), [eventLocale]);

  // ============================================================================
  // Pre-fill: pid → getContactCollectorGroupDetailsByPersonId → { party, contacts }
  // TODO: Add guestKey query param support (guestKey → getContactsByGuestKeyList → Contact).
  // Structure is ready — mapContactToFormValues accepts the flat Contact shape.
  // Priority: guestKey > pid > blank form (create new).
  // ============================================================================
  const pid = useMemo(() => new URLSearchParams(location.search).get('pid'), [location.search]);
  const firebaseEventId = telemetryQueryData?.eventById?.firebaseId;
  const contactCollectionId = data?.getContactCollectionByMagicLink?.id;
  const isCollectHouseholdEnabled = !!data?.getContactCollectionByMagicLink?.collectHousehold;

  const { data: groupDetailsData, loading: isGroupDetailsLoading } = useGetContactCollectorGroupDetailsByPersonIdQuery({
    variables: {
      contactCollectionId: contactCollectionId!,
      eventId: eventId!,
      personId: pid!,
    },
    batchMode: 'fast',
    skip: !pid || !contactCollectionId || !eventId,
    onError: error => trackError('Query', error),
  });

  // When collectHousehold is disabled, show only the primary contact (first in array)
  // When enabled, show all group members
  const allContacts = groupDetailsData?.getContactCollectorGroupDetailsByPersonId?.contacts;
  const existingContacts = useMemo(() => {
    if (!allContacts || allContacts.length === 0) return null;
    return isCollectHouseholdEnabled ? allContacts : [allContacts[0]];
  }, [allContacts, isCollectHouseholdEnabled]);

  const partyString = groupDetailsData?.getContactCollectorGroupDetailsByPersonId?.party ?? null;

  // Track original values by contactId (stable across array mutations)
  const originalValuesMapRef = useRef<Map<string, ContactInputFields>>(new Map());
  const hasPrefilledRef = useRef(false);

  // ============================================================================

  const ogImage = getImageSource(data?.getContactCollectionByMagicLink?.mediaType as VisualType, data?.getContactCollectionByMagicLink?.mediaSource ?? '');
  const ogTitle = data?.getContactCollectionByMagicLink?.message || tMetatags.title;
  const ogUrl = `https://withjoy.com/contact/${magicLinkId}`;

  const [createContactsMutation, { loading: isCreateContactsMutationLoading, error: createContactsMutationError }] = useCreateContactsWithoutTagsMutation();
  const [updateContactMutation, { loading: isUpdateContactMutationLoading, error: updateContactMutationError }] = useUpdateContactForContactCollectorMutation();

  const validationSchema = useMemo(() => yupSchema(tAdmin.required(), tAdmin.invalidEmail(), fieldSettings), [tAdmin, fieldSettings]);

  // fieldSettings shaping (OFF strip + address replication primary→household) lives in
  // payloadFromFieldSettings.ts; applied to every payload site below.

  const buildBaseTelemetry = (values: Contacts) => ({
    url: `https://withjoy.com/contact/${magicLinkId}`,
    eventHandle: telemetryQueryData?.eventById?.website ?? '',
    guestFirstName: values.contacts[0].firstName,
    guestLastName: values.contacts[0].lastName,
    guestEmail: values.contacts[0].email,
    // Only log fieldSettings when the feature is active — when off the form ran on legacy
    // fallback rules, so logging them would misrepresent the collector's configuration.
    ...(isFieldSettingsEnabled && {
      fieldSettings: {
        email: fieldSettings.email,
        phone: fieldSettings.phone,
        address: fieldSettings.address,
      },
    }),
  });

  // Prefilled-branch submit: updates existing contacts via `updateContact` (skipping unchanged),
  // then creates any new household members the guest added via `createContactsWithoutTags`.
  const submitPrefilledContacts = async (values: Contacts) => {
    const primary = values.contacts[0];
    const prefilledContacts = values.contacts.filter(c => c._prefilledContactId);

    const updatePromises: Promise<unknown>[] = [];
    for (const contact of prefilledContacts) {
      const original = originalValuesMapRef.current.get(contact._prefilledContactId!);
      const hasChanges = !original || (Object.keys(original) as Array<keyof ContactInputFields>).some(key => original[key] !== contact[key]);

      if (hasChanges) {
        const contactIndex = values.contacts.indexOf(contact);
        const updateInput = mapFormValuesToUpdateInput(contact, eventId ?? '', firebaseEventId ?? '');
        // Update path clears a blank address (sends an empty object) instead of omitting it,
        // so a guest who deletes their address actually clears the stored one.
        updateInput.address = buildAddress(values.contacts, contactIndex, fieldSettings, false);
        if (updateInput.contact) {
          updateInput.contact.email = emailFor(contact, fieldSettings);
        }
        updateInput.phoneNumbers = phoneNumbersFor(contact, fieldSettings);
        updatePromises.push(updateContactMutation({ variables: { id: contact._prefilledContactId!, data: updateInput } }));
      }
    }
    if (updatePromises.length > 0) {
      await Promise.all(updatePromises);
    }

    const newContacts = values.contacts.filter(c => !c._prefilledContactId);
    if (newContacts.length > 0) {
      const mutationData = newContacts.map(contact => {
        const index = values.contacts.indexOf(contact);
        return {
          address: buildAddress(values.contacts, index, fieldSettings),
          contact: {
            contactCollectionId,
            firebaseEventId: context.firebaseId,
            eventId,
            // Create path omits an empty email (undefined, not ''); household members aren't required to provide one.
            email: emailFor(contact, fieldSettings) || undefined,
            firstName: (contact.firstName ?? '').trim(),
            lastName: (contact.lastName ?? '').trim(),
            prefix: (contact.prefix ?? '').trim() || undefined,
            suffix: (contact.suffix ?? '').trim() || undefined,
            party: partyString ?? `${contactCollectionId}-${primary.firstName}-${primary.lastName}`,
            isNewPartyMember: true,
          },
          phoneNumbers: phoneNumbersFor(contact, fieldSettings),
        };
      });
      await createContactsMutation({ variables: { data: mutationData } });
    }

    buttonInteracted({
      buttonType: 'save',
      element: 'submitContactForm',
      extraInfo: {
        ...buildBaseTelemetry(values),
        prefilled: true,
        updated: updatePromises.length > 0,
        householdSize: values.contacts.length,
        newMembersAdded: newContacts.length,
      },
    });
  };

  // Fresh-branch submit: no `pid`, no existing contacts — create everything (primary + +1s)
  // in a single bulk `createContactsWithoutTags` call.
  const submitNewContacts = async (values: Contacts) => {
    if (!contactCollectionId) {
      throw Error('No Collection Id provided');
    }
    const primary = values.contacts[0];
    const mutationData = values.contacts.map((contact, index) => ({
      address: buildAddress(values.contacts, index, fieldSettings),
      contact: {
        contactCollectionId,
        firebaseEventId: context.firebaseId,
        eventId,
        // Create path omits an empty email (undefined, not ''); household members aren't required to provide one.
        email: emailFor(contact, fieldSettings) || undefined,
        firstName: (contact.firstName ?? '').trim(),
        lastName: (contact.lastName ?? '').trim(),
        prefix: (contact.prefix ?? '').trim() || undefined,
        suffix: (contact.suffix ?? '').trim() || undefined,
        party: values.contacts.length > 1 ? `${contactCollectionId}-${primary.firstName}-${primary.lastName}` : undefined,
      },
      phoneNumbers: phoneNumbersFor(contact, fieldSettings),
    }));
    await createContactsMutation({ variables: { data: mutationData } });

    buttonInteracted({
      buttonType: 'save',
      element: 'submitContactForm',
      extraInfo: buildBaseTelemetry(values),
    });
  };

  const formik = useFormik<Contacts>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async values => {
      try {
        const hasPrefilled = values.contacts.some(c => c._prefilledContactId);
        if (hasPrefilled) {
          await submitPrefilledContacts(values);
        } else {
          await submitNewContacts(values);
        }
        setE164PhoneNumber({ 0: '' });
        routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.done.path, location.search);
      } catch (error) {
        trackError('Mutation', error);
      }
    },
  });

  // Pre-fill form when existing contact data arrives (single or household members)
  useEffect(() => {
    if (existingContacts && existingContacts.length > 0 && !hasPrefilledRef.current) {
      const prefilledContacts = existingContacts.map((contact, index) => {
        const formValues = mapContactToFormValues(contact);
        const withTracking = {
          ...formValues,
          _prefilledContactId: contact.id,
          _primaryContact: index === 0,
        };
        // Non-head members: use space chars for address fields (bypass Yup required validation)
        if (index > 0) {
          return { ...withTracking, address1: ' ', postalCode: ' ', city: ' ', region: ' ' };
        }
        return withTracking;
      });

      formik.setValues({ contacts: prefilledContacts });

      // Track originals by contactId (stable across array mutations)
      prefilledContacts.forEach(contact => {
        if (contact._prefilledContactId) {
          originalValuesMapRef.current.set(contact._prefilledContactId, contact);
        }
      });

      hasPrefilledRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingContacts]);

  // Apply locale-derived country defaults when locale loads (only for fresh forms)
  useEffect(() => {
    if (!eventLocale || hasPrefilledRef.current) return;
    const contacts = formik.values.contacts.map(contact => {
      // Only update country if it's still the static default (US)
      if (contact.countryCode === 'US' && contact.country === 'United States') {
        return {
          ...contact,
          country: localeCountryDefaults.country,
          countryCode: localeCountryDefaults.countryCode,
        };
      }
      return contact;
    });
    formik.setValues({ contacts });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventLocale, localeCountryDefaults]);

  const isMutationLoading = isCreateContactsMutationLoading || isUpdateContactMutationLoading;
  const hasMutationError = !!createContactsMutationError || !!updateContactMutationError;
  const isPrefilledContactLoading = !!pid && isGroupDetailsLoading;

  return {
    tAdmin,
    showErrorPage: !!error,
    isContactCollectionInactive,
    eventHandle: telemetryQueryData?.eventById?.website ?? '',
    isLoading: isTelemetryQueryLoading || isPrefilledContactLoading,
    data,
    noData: data?.getContactCollectionByMagicLink === null,
    ogImage,
    ogTitle,
    ogUrl,
    formik,
    context,
    setContext,
    isMutationLoading,
    hasMutationError,
    telemetryData: {
      postgresEventId: telemetryQueryData?.eventById?.id ?? '',
      firebaseEventId: telemetryQueryData?.eventById?.firebaseId ?? '',
      joyEventType: telemetryQueryData?.eventById?.info.eventType ?? '',
    },
    e164PhoneNumber,
    setE164PhoneNumber,
    emailRule,
    phoneNumberRule,
    mailingAddressRule,
    phoneCountryCode: localeCountryDefaults.phoneCountryCode,
  };
};
